// src/services/woserviceApi.ts
import { ENV } from "@app/constants/env";
import { WoserviceCreateType } from "@feature/types/wo.service.create.type";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { getToken } from "@app/helpers/authProvider";

type ApiResponse<T> = {
  data: T;
  success: boolean;
  message?: string;
};

const API_BASE_URL = `${ENV.BASE_API_URL +ENV.JOB_APIURL}/WorkOrderService`;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
});

const authBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (incomingArgs, api, extraOptions) => {
  let args: string | FetchArgs = incomingArgs;

  if (typeof args === "string") {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      const headers =
        (args as FetchArgs).headers instanceof Headers
          ? (args as FetchArgs).headers
          : new Headers((args as FetchArgs).headers);

      headers.set("Authorization", `Bearer ${token}`);
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // refresh token flow could go here
    }

    return result;
  } catch {
    return { error: { status: 500, data: "Token retrieval failed" } } as any;
  }
};

// Helper function to sanitize date fields
const sanitizeServiceData = (services: WoserviceCreateType[]) => {
  return services.map((service) => ({
    ...service,
    requireddate: service.requireddate || null,
    eta: service.eta || null,
    etd: service.etd || null,
    timefrom: service.timefrom || null,
    timeto: service.timeto || null,
  }));
};

export const woserviceApi = createApi({
  reducerPath: 'servicesApi',
  baseQuery: authBaseQuery,
  tagTypes: ["WorkOrderService"],
  endpoints: (builder) => ({
    createMultipleService: builder.mutation<
      ApiResponse<number[]>,
      WoserviceCreateType[]
    >({
      query: (services) => ({
        url: '/CreateMultipleService',
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: sanitizeServiceData(services), // Send as array, not wrapped in object
      }),
      invalidatesTags: ["WorkOrderService"],
      transformResponse: (response: any) => {
        // Handle both wrapped and unwrapped responses
        if (response?.data) {
          return response;
        }
        return { data: response, success: true };
      },
    }),
  }),
});

export const { useCreateMultipleServiceMutation } = woserviceApi;
